import {
  type ComponentType,
  type Dispatch,
  type SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react'

import { type OnlyFansList } from '@api'
import debounce from 'lodash/debounce'
import times from 'lodash/times'
import {
  type ListChildComponentProps,
  ListOnItemsRenderedProps,
  VariableSizeList,
} from 'react-window'

import { IconLoading } from '@/assets/icons/icon-loading'
import { EmptyTableContent } from '@/components/ui/empty/empty-table-content.tsx'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Radio, RadioGroup } from '@/components/ui/radio'
import { SearchInput } from '@/components/ui/search-input.tsx'
import { Tooltip } from '@/components/ui/tooltip.tsx'
import {
  usersListOrderOptions,
  usersListSortOptions,
} from '@/modules/chatting/consts/usersList.ts'
import SortIcon from '@/modules/chatting/icons/sort.svg'
import type { GetUsersListFilters } from '@/modules/chatting/types'
import {
  NewMessageFan,
  NewMessageUsersListItem,
} from '@/modules/chatting/ui/components/NewMessageUsersListItem/NewMessageUsersListItem.tsx'
import { NewMessageUsersListItemSkeleton } from '@/modules/chatting/ui/components/NewMessageUsersListItem/NewMessageUsersListItemSkeleton.tsx'
import { UsersListFilters } from '@/modules/chatting/ui/components/UsersListFilters/UsersListFilters.tsx'
import { IconButton } from '@/modules/chatting/ui/components/icon-button'
import { useSizeObserver } from '@/modules/chatting/ui/sidebar/use-size-observer'
import { classNames } from '@/utils/helpers'

import { IncludableLists } from './IncludableLists.tsx'

interface Props {
  isOpen: boolean
  includedLists: OnlyFansList['id'][]
  setIncludedLists: Dispatch<SetStateAction<OnlyFansList['id'][]>>
  excludedLists: OnlyFansList['id'][]
  setExcludedLists: Dispatch<SetStateAction<OnlyFansList['id'][]>>
  query: string
  setQuery: Dispatch<SetStateAction<string>>
  sort: string
  setSort: Dispatch<SetStateAction<string>>
  order: string
  setOrder: Dispatch<SetStateAction<string>>
  fans?: NewMessageFan[]

  isLoading: boolean
  onFetchNext: () => void
  isFetchingNextPage?: boolean
  selectedFans: NewMessageFan['id'][]
  toggleChatterSelection: (fan: NewMessageFan) => void
  className?: string
  filters: GetUsersListFilters
  setFilters: Dispatch<SetStateAction<GetUsersListFilters>>
}

export const UsersList = ({
  isOpen,
  query,
  setQuery,
  includedLists,
  setIncludedLists,
  excludedLists,
  setExcludedLists,
  sort,
  setSort,
  order,
  setOrder,
  fans,
  isLoading,
  onFetchNext,
  isFetchingNextPage,
  selectedFans,
  toggleChatterSelection,
  className,
  filters,
  setFilters,
}: Props) => {
  const [search, setSearch] = useState('')
  const [parentRef, width, height] = useSizeObserver()

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSearch = useCallback(
    debounce(
      (value) => (!value.length || value.length > 2) && setQuery(value),
      500,
    ),
    [setQuery],
  )

  const handleInputChange = useCallback(
    (value: string) => {
      setSearch(value)
      handleSearch(value)
    },
    [handleSearch],
  )

  const renderChatItem: ComponentType<
    ListChildComponentProps<NewMessageFan[]>
  > = useCallback(
    ({ data, index, style }) => (
      <NewMessageUsersListItem
        enableMultiSelect
        onClick={toggleChatterSelection}
        chatter={data[index]}
        style={style}
      />
    ),
    [toggleChatterSelection],
  )

  const getItemSize = useCallback(() => 82, [])

  const toggleList = useCallback(
    (listId: OnlyFansList['id'], selectedLists: OnlyFansList['id'][]) => {
      const isSelected = selectedLists.includes(listId)

      if (isSelected) {
        return selectedLists.filter((id) => id !== listId)
      }

      return [...selectedLists, listId]
    },
    [],
  )

  const toggleIncludedList = useCallback(
    (listId: OnlyFansList['id']) => {
      setIncludedLists((prev) => {
        const newLists = toggleList(listId, prev)

        setSearch('')
        setQuery('')

        return newLists
      })
    },
    [setIncludedLists, toggleList, setQuery, setSearch],
  )

  const toggleExcludedList = useCallback(
    (listId: OnlyFansList['id']) => {
      setExcludedLists((prev) => toggleList(listId, prev))
    },
    [setExcludedLists, toggleList],
  )

  const onItemsRendered = useCallback(
    ({ visibleStopIndex }: ListOnItemsRenderedProps) => {
      if (visibleStopIndex === (fans?.length ?? 0) - 1) {
        onFetchNext()
      }
    },
    [fans?.length, onFetchNext],
  )

  const fansToRender = useMemo<NewMessageFan[]>(
    () =>
      (fans ?? []).map((fan) =>
        selectedFans.includes(fan.id) ? { ...fan, isSelected: true } : fan,
      ),
    [fans, selectedFans],
  )

  return (
    <div
      className={classNames(
        'w-full pt-4 md:max-w-[340px] md:border-r',
        className,
      )}
    >
      <div className="mb-4 space-y-4 px-4">
        <IncludableLists
          isOpen={isOpen}
          title="Send to"
          selectedItems={includedLists}
          onToggleSelect={toggleIncludedList}
          onSelectValues={setIncludedLists}
          disabledItems={excludedLists}
        />
        <Separator />
        <IncludableLists
          isOpen={isOpen}
          title="Exclude"
          selectedItems={excludedLists}
          onToggleSelect={toggleExcludedList}
          onSelectValues={setExcludedLists}
          disabledItems={includedLists}
        />
        <Separator />
        <div className="flex items-center gap-3">
          <SearchInput
            placeholder="Search"
            value={search}
            onInputChange={handleInputChange}
          />
          <UsersListFilters
            filters={filters}
            setFilters={setFilters}
            query={query}
          />
          <Popover>
            <PopoverTrigger
              className="outline-none"
              asChild
            >
              <Tooltip
                content="Sort by"
                asChild
              >
                <IconButton icon={SortIcon} />
              </Tooltip>
            </PopoverTrigger>
            <PopoverContent className="z-100 w-[200px] p-2">
              <RadioGroup
                value={order}
                onChange={setOrder}
              >
                {usersListOrderOptions.map((option) => (
                  <Radio
                    className="p-1.5"
                    key={option.value}
                    labelClassName="text-sm font-medium leading-[22px]"
                    {...option}
                  />
                ))}
              </RadioGroup>
              <Separator />
              <RadioGroup
                value={sort}
                onChange={setSort}
              >
                {usersListSortOptions.map((option) => (
                  <Radio
                    className="p-1.5"
                    key={option.value}
                    labelClassName="text-sm font-medium leading-[22px]"
                    {...option}
                  />
                ))}
              </RadioGroup>
            </PopoverContent>
          </Popover>
        </div>
      </div>
      <div
        className="relative min-h-[288px] w-full flex-1"
        ref={parentRef}
      >
        {isLoading ? (
          times(3).map((num) => (
            <NewMessageUsersListItemSkeleton
              withMultiSelection
              key={num}
            />
          ))
        ) : !fans || fans.length === 0 ? (
          <EmptyTableContent>
            No results found. <br />
            Please change filter criteria.
          </EmptyTableContent>
        ) : (
          <VariableSizeList
            className="scrollbar-hide"
            itemData={fansToRender}
            itemSize={getItemSize}
            height={height}
            itemCount={fans?.length ?? 0}
            width={width}
            onItemsRendered={onItemsRendered}
          >
            {renderChatItem}
          </VariableSizeList>
        )}
        {isFetchingNextPage && (
          <div className="absolute bottom-0 left-0 right-0 bg-card py-1">
            <IconLoading className="mx-auto animate-spin" />
          </div>
        )}
      </div>
    </div>
  )
}

const Separator = () => <div className="w-full border-b" />
